if (!customElements.get('store-locator')) {
  class StoreLocator extends HTMLElement {
    async connectedCallback() {
      this.mapEl = this.querySelector('[data-store-locator-map]');
      this.listEl = this.querySelector('[data-store-locator-list]');
      this.emptyEl = this.querySelector('[data-store-locator-empty]');
      this.statusEl = this.querySelector('[data-store-locator-status]');
      this.searchForm = this.querySelector('[data-store-locator-search-form]');
      this.searchInput = this.querySelector('[data-store-locator-search]');
      this.categorySelect = this.querySelector('[data-store-locator-category]');

      this.defaultColour = this.dataset.defaultColour || '#1a1a1a';
      this.geocodeCountry = this.dataset.geocodeCountry || '';
      this.searchRadiusKm = Number(this.dataset.searchRadiusKm) || 50;
      this.searchCenter = null;
      this.markersById = new Map();

      this.initMap();

      this.setStatus('Loading stockists…');
      const allStockists = await this.fetchAllStockists();
      this.setStatus('');

      this.stockists = allStockists.filter((s) => this.hasCoords(s));
      this.pendingGeocode = allStockists.filter((s) => !this.hasCoords(s) && this.addressLine(s));

      this.populateCategories(allStockists);
      this.render(this.stockists);
      this.geocodeMissing();

      this.searchInput.addEventListener('input', this.handleSearchInput.bind(this));
      this.searchForm.addEventListener('submit', this.handleSearchSubmit.bind(this));
      this.categorySelect.addEventListener('change', this.applyFilters.bind(this));
    }

    handleSearchInput() {
      // Typing after a location search invalidates it - the instant text filter
      // takes back over immediately, while a debounced geocode attempt runs in
      // the background and upgrades to distance-sorted results if it resolves.
      if (this.searchCenter) {
        this.searchCenter = null;
        this.setStatus('');
      }
      this.debouncedApplyFilters();
      this.debouncedLocationSearch();
    }

    debouncedApplyFilters() {
      if (!this._debouncedApplyFilters) {
        this._debouncedApplyFilters = this.debounce(this.applyFilters.bind(this), 150);
      }
      this._debouncedApplyFilters();
    }

    debouncedLocationSearch() {
      if (!this._debouncedLocationSearch) {
        // Longer than the text-filter debounce, and long enough to avoid firing
        // a geocode request per keystroke - stays well within Nominatim's ~1
        // request/second usage policy even for fast typers.
        this._debouncedLocationSearch = this.debounce(() => this.attemptLocationSearch(false), 600);
      }
      this._debouncedLocationSearch();
    }

    async handleSearchSubmit(evt) {
      evt.preventDefault();
      const submitBtn = this.searchForm.querySelector('button[type="submit"]');
      submitBtn.disabled = true;
      this.setStatus('Searching…');
      await this.attemptLocationSearch(true);
      submitBtn.disabled = false;
    }

    /**
     * @param {boolean} showErrors - Show a "couldn't find that" status message
     *   on failure. Used for explicit submits; suppressed for live-as-you-type
     *   attempts, since a mid-word query failing to geocode is expected and
     *   shouldn't interrupt the live text-filter results already on screen.
     */
    async attemptLocationSearch(showErrors) {
      const query = this.searchInput.value.trim();
      if (!query) {
        this.searchCenter = null;
        this.setStatus('');
        this.applyFilters();
        return;
      }

      const requestId = (this.searchRequestId = (this.searchRequestId || 0) + 1);
      let coords = null;
      try {
        coords = await this.geocodeAddress(query);
      } catch (error) {
        console.warn('[store-locator] Search geocode failed', error);
      }

      // Ignore stale responses if the query has moved on since this request started.
      if (requestId !== this.searchRequestId || this.searchInput.value.trim() !== query) return;

      if (!coords) {
        this.searchCenter = null;
        if (showErrors) {
          this.setStatus(`Couldn't find "${query}" - showing name/suburb matches instead.`);
        }
        this.applyFilters();
        return;
      }

      this.searchCenter = coords;
      this.applyFilters();
    }

    /**
     * The simple `shop.metaobjects.<type>.values` Liquid accessor silently caps
     * at 50 entries, which this store's stockist count blows well past. Fetching
     * client-side via the Storefront API instead, with proper cursor pagination,
     * using a "Public access token" (Settings > Apps and sales channels >
     * Headless channel > Storefront API) - that token type is designed to be
     * exposed client-side and only grants read access to storefront-visible data.
     */
    async fetchAllStockists() {
      const token = this.dataset.storefrontToken;
      const shopDomain = this.dataset.shopDomain;
      if (!token || !shopDomain) return [];

      const url = `https://${shopDomain}/api/2026-07/graphql.json`;
      const query = `
        query Stockists($cursor: String) {
          metaobjects(type: "stockists", first: 250, after: $cursor) {
            nodes {
              id
              fields { key value }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      `;

      const all = [];
      let cursor = null;

      while (true) {
        let result;
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({ query, variables: { cursor } })
          });
          result = await response.json();
        } catch (error) {
          console.warn('[store-locator] Failed to fetch stockists', error);
          break;
        }

        const connection = result?.data?.metaobjects;
        if (!connection) {
          console.warn('[store-locator] Unexpected Storefront API response', result);
          break;
        }

        connection.nodes.forEach((node) => {
          const fields = {};
          node.fields.forEach((field) => {
            fields[field.key] = field.value;
          });

          all.push({
            id: node.id,
            name: fields.name || '',
            address: fields.address || '',
            suburb: fields.suburb || '',
            state: fields.state || '',
            postcode: fields.postcode || '',
            phone: fields.phone || '',
            website: fields.website || null,
            lat: fields.latitude != null ? Number(fields.latitude) : null,
            lng: fields.longitude != null ? Number(fields.longitude) : null,
            category: fields.category || null,
            colour: fields.marker_colour || null
          });
        });

        if (!connection.pageInfo.hasNextPage) break;
        cursor = connection.pageInfo.endCursor;
      }

      return all;
    }

    hasCoords(stockist) {
      return (
        stockist.lat != null &&
        stockist.lng != null &&
        Number.isFinite(Number(stockist.lat)) &&
        Number.isFinite(Number(stockist.lng))
      );
    }

    addressLine(stockist) {
      return [stockist.address, stockist.suburb, stockist.state, stockist.postcode]
        .filter(Boolean)
        .join(', ');
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }

    initMap() {
      const lat = Number(this.dataset.defaultLat) || 0;
      const lng = Number(this.dataset.defaultLng) || 0;
      const zoom = Number(this.dataset.defaultZoom) || 4;

      this.map = L.map(this.mapEl).setView([lat, lng], zoom);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);
    }

    populateCategories(allStockists) {
      const categories = Array.from(
        new Set(allStockists.map((s) => (s.category || '').trim()).filter(Boolean))
      ).sort();

      categories.forEach((category) => {
        const option = document.createElement('option');
        option.value = category;
        option.textContent = category;
        this.categorySelect.appendChild(option);
      });
    }

    setStatus(text) {
      this.statusEl.textContent = text;
      this.statusEl.hidden = !text;
    }

    /**
     * Stockists without saved coordinates are located one at a time via
     * OpenStreetMap's free Nominatim lookup, spaced out to respect its ~1
     * request/second usage policy. Results aren't persisted (this theme has no
     * write access to the metaobject) - the popup surfaces the resolved
     * coordinates so they can be copied back into the stockist's admin entry.
     */
    async geocodeMissing() {
      const queue = this.pendingGeocode;
      if (queue.length === 0) return;

      let remaining = queue.length;
      this.setStatus(`Locating ${remaining} ${remaining === 1 ? 'address' : 'addresses'} without saved coordinates…`);

      for (const stockist of queue) {
        try {
          const coords = await this.geocodeAddress(this.addressLine(stockist));
          if (coords) {
            stockist.lat = coords.lat;
            stockist.lng = coords.lng;
            stockist.autoLocated = true;
            this.stockists.push(stockist);
            this.applyFilters();
          } else {
            console.warn(`[store-locator] Could not locate address: ${this.addressLine(stockist)}`);
          }
        } catch (error) {
          console.warn(`[store-locator] Geocoding failed for: ${this.addressLine(stockist)}`, error);
        }

        remaining -= 1;
        this.setStatus(
          remaining > 0
            ? `Locating ${remaining} ${remaining === 1 ? 'address' : 'addresses'} without saved coordinates…`
            : ''
        );

        if (remaining > 0) await this.wait(1100);
      }
    }

    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async geocodeAddress(address) {
      const query = this.geocodeCountry ? `${address}, ${this.geocodeCountry}` : address;
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;

      const results = await response.json();
      if (!results.length) return null;

      return { lat: Number(results[0].lat), lng: Number(results[0].lon) };
    }

    static haversineKm(lat1, lng1, lat2, lng2) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const earthRadiusKm = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLng = toRad(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
      return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    applyFilters() {
      const category = this.categorySelect.value;
      const byCategory = (s) => !category || s.category === category;

      let filtered;

      if (this.searchCenter) {
        const { lat: centerLat, lng: centerLng } = this.searchCenter;
        filtered = this.stockists
          .filter(byCategory)
          .map((s) => {
            s.distanceKm = StoreLocator.haversineKm(centerLat, centerLng, Number(s.lat), Number(s.lng));
            return s;
          })
          .filter((s) => s.distanceKm <= this.searchRadiusKm)
          .sort((a, b) => a.distanceKm - b.distanceKm);

        this.setStatus(
          filtered.length > 0
            ? `${filtered.length} ${filtered.length === 1 ? 'stockist' : 'stockists'} within ${this.searchRadiusKm}km`
            : `No stockists within ${this.searchRadiusKm}km of that search.`
        );
      } else {
        this.stockists.forEach((s) => {
          delete s.distanceKm;
        });

        const query = this.searchInput.value.trim().toLowerCase();
        filtered = this.stockists.filter((s) => {
          if (!byCategory(s)) return false;
          if (!query) return true;

          const haystack = [s.name, s.suburb, s.postcode, s.state, s.address]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(query);
        });
      }

      this.render(filtered);
    }

    render(stockists) {
      this.markersById.forEach((marker) => this.map.removeLayer(marker));
      this.markersById.clear();
      this.listEl.querySelectorAll('.store-locator__item').forEach((el) => el.remove());

      this.emptyEl.hidden = stockists.length > 0;

      const bounds = [];

      stockists.forEach((stockist) => {
        const colour = stockist.colour || this.defaultColour;
        const lat = Number(stockist.lat);
        const lng = Number(stockist.lng);

        const marker = L.circleMarker([lat, lng], {
          radius: 8,
          weight: 2,
          color: '#fff',
          fillColor: colour,
          fillOpacity: 1
        }).addTo(this.map);

        marker.bindPopup(this.popupHtml(stockist));
        marker.on('click', () => this.setActive(stockist.id));

        this.markersById.set(stockist.id, marker);
        bounds.push([lat, lng]);

        this.listEl.appendChild(this.listItem(stockist, colour));
      });

      if (bounds.length > 0) {
        this.map.fitBounds(bounds, { padding: [32, 32], maxZoom: 12 });
      }
    }

    popupHtml(stockist) {
      const parts = [`<div class="store-locator__popup"><strong>${this.escape(stockist.name)}</strong>`];
      if (stockist.address) parts.push(this.escape(stockist.address));
      const cityLine = [stockist.suburb, stockist.state, stockist.postcode].filter(Boolean).join(' ');
      if (cityLine) parts.push(`<br>${this.escape(cityLine)}`);
      if (stockist.phone) parts.push(`<br>${this.escape(stockist.phone)}`);
      if (stockist.website) {
        parts.push(`<br><a href="${this.escape(stockist.website)}" target="_blank" rel="noopener">Visit website</a>`);
      }
      if (stockist.autoLocated) {
        parts.push(
          `<br><span class="store-locator__popup-auto">Auto-located · ${stockist.lat.toFixed(5)}, ${stockist.lng.toFixed(5)}<br>Save these in the admin to skip lookup next time.</span>`
        );
      }
      parts.push('</div>');
      return parts.join('');
    }

    listItem(stockist, colour) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'store-locator__item';
      button.dataset.id = stockist.id;

      const cityLine = [stockist.suburb, stockist.state, stockist.postcode].filter(Boolean).join(' ');

      const distanceLabel = stockist.distanceKm != null ? `${stockist.distanceKm.toFixed(1)} km away` : '';

      button.innerHTML = `
        <div class="store-locator__item-name">
          <span class="store-locator__item-dot" style="background-color: ${this.escape(colour)}" aria-hidden="true"></span>
          ${this.escape(stockist.name)}
          ${distanceLabel ? `<span class="store-locator__item-distance">${this.escape(distanceLabel)}</span>` : ''}
        </div>
        <div class="store-locator__item-address">${this.escape(stockist.address)}${cityLine ? `, ${this.escape(cityLine)}` : ''}</div>
        <div class="store-locator__item-meta">
          ${stockist.phone ? this.escape(stockist.phone) : ''}
          ${stockist.website ? ` &middot; <a href="${this.escape(stockist.website)}" target="_blank" rel="noopener">Website</a>` : ''}
        </div>
      `;

      button.addEventListener('click', () => {
        const marker = this.markersById.get(stockist.id);
        if (!marker) return;
        this.map.setView(marker.getLatLng(), Math.max(this.map.getZoom(), 11));
        marker.openPopup();
        this.setActive(stockist.id);
      });

      return button;
    }

    setActive(id) {
      this.listEl.querySelectorAll('.store-locator__item').forEach((el) => {
        el.setAttribute('aria-current', String(el.dataset.id === id));
      });
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }
  }

  customElements.define('store-locator', StoreLocator);
}
